import { useEffect } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { needsCurrencyMigration } from '@components/Web3/utils'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import {
    SpaceAddressFromSpaceId,
    useGetSingleSubscription,
    useSpaceDataWithId,
} from 'use-towns-client'

import { useMyMembershipStatus } from '../RenewMembership/hooks/useMyMembershipStatus'

import { useNavigateToSubscription } from './hooks/useNavigateToSubscription'
import { useMembershipSubscriptionsStore } from './membershipSubscriptions.store'

const shownToastsThisSession = new Set<string>()

/**
 * Component that shows a toast notification when a user has an active ETH subscription
 * for a space that has been converted to USDC.
 */
export function SubscriptionMigrationToast({ spaceId }: { spaceId: string }) {
    const { data: myAddress } = useMyAbstractAccountAddress()
    const { data: membershipStatus } = useMyMembershipStatus({ spaceId })
    const { data: membershipInfo } = useReadableMembershipInfo(spaceId)
    const navigateToSubscription = useNavigateToSubscription()

    const spaceAddress = SpaceAddressFromSpaceId(spaceId)

    const spaceData = useSpaceDataWithId(spaceId)

    const { data: subscription } = useGetSingleSubscription({
        walletAddress: myAddress,
        spaceAddress: spaceAddress,
        tokenId: membershipStatus?.tokenId ?? '',
    })

    const addCheckedMigrationSpace = useMembershipSubscriptionsStore(
        (s) => s.addCheckedMigrationSpace,
    )

    useEffect(() => {
        // Skip if missing required data
        if (!spaceAddress || !subscription || !membershipInfo?.currency) {
            return
        }

        // If subscription is inactive or no migration needed, suppress future checks
        // Inactive subscriptions will be handled correctly when user enables auto-renewal
        if (
            !subscription.active ||
            !needsCurrencyMigration(subscription.currency, membershipInfo.currency)
        ) {
            addCheckedMigrationSpace(spaceAddress)
            return
        }

        // Skip if toast is already showing for this space in this session
        if (shownToastsThisSession.has(spaceAddress)) {
            return
        }

        // Mark toast as showing for this session
        shownToastsThisSession.add(spaceAddress)

        // Show the toast
        popupToast(
            ({ toast }) => (
                <StandardToast
                    toast={toast}
                    icon="alert"
                    message={`${spaceData?.name ?? 'This town'} now uses USDC. Update your subscription to continue auto-renewal.`}
                    cta="Update Subscription"
                    ctaColor="cta2"
                    onCtaClick={({ dismissToast }) => {
                        addCheckedMigrationSpace(spaceAddress)
                        navigateToSubscription(spaceId)
                        dismissToast()
                    }}
                    onDismiss={() => {
                        addCheckedMigrationSpace(spaceAddress)
                    }}
                />
            ),
            {
                duration: Number.POSITIVE_INFINITY,
            },
        )
    }, [
        spaceId,
        spaceAddress,
        subscription,
        membershipInfo?.currency,
        addCheckedMigrationSpace,
        navigateToSubscription,
        spaceData?.name,
    ])

    return null
}
